import { useState, useEffect, useCallback } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import GlobalSearchModal from '@/components/GlobalSearchModal'
import ResourceMonitor from '@/components/ResourceMonitor'
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Dropdown,
  Space,
  Breadcrumb,
  Switch,
  ConfigProvider,
  theme,
  Modal,
  Descriptions,
  Tag,
  Badge,
  Popover,
  List,
  Typography,
  Empty,
  Spin,
} from 'antd'
import axios from '@/utils/axios'
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  SearchOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SunOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  GiftOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  TeamOutlined,
  LockOutlined,
  DeleteOutlined,
  PauseCircleOutlined,
  SplitCellsOutlined,
  WarningOutlined,
  CloudServerOutlined,
  AppstoreOutlined,
  StopOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  HistoryOutlined,
  ContainerOutlined,
  LineChartOutlined,
  SyncOutlined,
  DatabaseOutlined,
  BarcodeOutlined,
  ShoppingOutlined,
  FileDoneOutlined,
  ImportOutlined,
  UnorderedListOutlined,
  ShopOutlined,
  CalendarOutlined as CalendarViewOutlined,
  TagOutlined,
  BellOutlined,
  ApiOutlined,
  LinkOutlined,
  CloudSyncOutlined,
  DownloadOutlined,
  FolderOutlined,
  EditOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'

const { Header, Sider, Content, Footer } = Layout
const { Text } = Typography

interface Notification {
  id: number
  type: string
  type_display: string
  title: string
  message: string
  link_url: string | null
  is_read: boolean
  created_at: string
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode')
    return saved === 'true'
  })
  const [, setIsMobile] = useState(window.innerWidth < 768)
  const [searchModalOpen, setSearchModalOpen] = useState(false)

  // 알림 관련 상태
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [notificationLoading, setNotificationLoading] = useState(false)
  const [notificationPopoverOpen, setNotificationPopoverOpen] = useState(false)

  const { user, logout, hasMenuPermission } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // 반응형 처리
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) {
        setCollapsed(true)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // 다크모드 저장
  useEffect(() => {
    localStorage.setItem('darkMode', String(isDarkMode))
  }, [isDarkMode])

  // 알림 읽지 않은 개수 조회
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get('/api/notifications/unread-count/')
      setUnreadCount(response.data.count)
    } catch (error) {
      console.error('알림 개수 조회 실패:', error)
    }
  }, [])

  // 알림 목록 조회
  const fetchNotifications = useCallback(async () => {
    setNotificationLoading(true)
    try {
      const response = await axios.get('/api/notifications/')
      setNotifications(response.data)
    } catch (error) {
      console.error('알림 목록 조회 실패:', error)
    } finally {
      setNotificationLoading(false)
    }
  }, [])

  // 알림 읽음 처리
  const markAsRead = async (id: number) => {
    try {
      await axios.post(`/api/notifications/${id}/read/`)
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('알림 읽음 처리 실패:', error)
    }
  }

  // 모든 알림 읽음 처리
  const markAllAsRead = async () => {
    try {
      await axios.post('/api/notifications/mark-all-read/')
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('알림 전체 읽음 처리 실패:', error)
    }
  }

  // 알림 클릭 처리
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
    if (notification.link_url) {
      navigate(notification.link_url)
      setNotificationPopoverOpen(false)
    }
  }

  // 페이지 로드 시 알림 개수 조회 + WebSocket 연결
  useEffect(() => {
    fetchUnreadCount()
    // 30초마다 알림 개수 갱신 (WebSocket 백업용)
    const interval = setInterval(fetchUnreadCount, 30000)

    // WebSocket 연결 (실시간 알림)
    const token = localStorage.getItem('accessToken')
    if (token) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = `${wsProtocol}//${window.location.host}/ws/stock/?token=${token}`
      const ws = new WebSocket(wsUrl)

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'notification_update') {
            // 실시간 알림 수신
            setUnreadCount(prev => prev + 1)
            // 알림 목록이 열려 있으면 새 알림 추가
            if (notificationPopoverOpen) {
              const newNotification: Notification = {
                id: data.notification_id,
                type: data.notification_type,
                type_display: data.notification_type === 'inbound' ? '입고 알림' :
                              data.notification_type === 'low_stock' ? '재고 부족' :
                              data.notification_type === 'po_approval' ? '발주 승인' : '시스템',
                title: data.title,
                message: data.message,
                link_url: data.link_url,
                is_read: false,
                created_at: data.created_at,
              }
              setNotifications(prev => [newNotification, ...prev])
            }
            // 브라우저 알림 (선택적)
            if (Notification.permission === 'granted') {
              new Notification(data.title, { body: data.message })
            }
          }
        } catch (e) {
          // JSON 파싱 오류 무시
        }
      }

      ws.onerror = () => {
        console.log('[WebSocket] 알림 연결 오류')
      }

      return () => {
        clearInterval(interval)
        ws.close()
      }
    }

    return () => clearInterval(interval)
  }, [fetchUnreadCount, notificationPopoverOpen])

  // 팝오버 열릴 때 알림 목록 조회
  useEffect(() => {
    if (notificationPopoverOpen) {
      fetchNotifications()
    }
  }, [notificationPopoverOpen, fetchNotifications])

  // URL 경로 → 권한 키 매핑
  const ROUTE_PERMISSION_MAP: { [path: string]: string } = {
    '/dashboard': 'dashboard',
    '/orders': 'orders.list',
    '/orders/dashboard': 'orders.dashboard',
    '/orders/pending': 'orders.pending',
    '/orders/verification': 'orders.verification',
    '/orders/inspected': 'orders.inspected',
    '/orders/completed': 'orders.completed',
    '/orders/cancelled': 'orders.cancelled',
    '/orders/prepacked': 'orders.prepacked',
    '/orders/split-delivery': 'orders.split_delivery',
    '/orders/unregistered': 'orders.unregistered',
    '/orders/3pl': 'orders.3pl',
    '/orders/problem': 'orders.problem',
    '/orders/hold': 'orders.hold',
    '/orders/trash': 'orders.trash',
    '/orders/inspection': 'orders.inspection',
    '/purchase/dashboard': 'purchase.dashboard',
    '/purchase/needed': 'purchase.needed',
    '/purchase/approval': 'purchase.approval',
    '/purchase/orders': 'purchase.orders',
    '/purchase/incoming': 'purchase.incoming',
    '/purchase/internet': 'purchase.internet',
    '/purchase/suppliers': 'purchase.suppliers',
    '/purchase/price-management': 'purchase.price_management',
    '/inventory/dashboard': 'inventory.dashboard',
    '/inventory/products': 'inventory.products',
    '/inventory/stop-sale': 'inventory.products',  // 판매중지/재입고 알림 (2026-10-22)
    '/inventory/inbound': 'inventory.inbound',
    '/inventory/outbound': 'inventory.outbound',
    '/inventory/history': 'inventory.history',
    '/inventory/analysis': 'inventory.analysis',
    '/inventory/past-quantity': 'inventory.past_quantity',
    '/inventory/adjustment': 'inventory.adjustment',
    '/inventory/barcode-print': 'inventory.barcode_print',
    '/inventory/barcode-mapping': 'inventory.barcode_mapping',
    '/inventory/data-management': 'inventory.data_management',
    '/settings': 'settings.general',
    '/settings/users': 'settings.users',
    '/settings/approval': 'settings.approval',
    '/settings/permissions': 'settings.permissions',
    // 쇼핑몰 연동 (super_admin only - 별도 처리)
    '/marketplace': 'marketplace',
    '/marketplace/dashboard': 'marketplace.dashboard',
    '/marketplace/accounts': 'marketplace.accounts',
    '/marketplace/orders': 'marketplace.orders',
    '/marketplace/tracking': 'marketplace.tracking',
    '/marketplace/products': 'marketplace.products',
    '/marketplace/register': 'marketplace.register',
    '/marketplace/stock-sync': 'marketplace.stock_sync',
  }

  // URL 권한 체크 및 리다이렉트
  useEffect(() => {
    const currentPath = location.pathname

    // 동적 경로 (예: /orders/:id, /inventory/products/:id) 는 부모 권한으로 체크
    let permissionKey = ROUTE_PERMISSION_MAP[currentPath]

    if (!permissionKey) {
      // 동적 경로 처리: /orders/123 → orders.list, /inventory/products/123 → inventory.products
      if (currentPath.match(/^\/orders\/[^/]+$/)) {
        permissionKey = 'orders.list'
      } else if (currentPath.match(/^\/inventory\/products\/[^/]+$/)) {
        permissionKey = 'inventory.products'
      } else if (currentPath.match(/^\/purchase\/orders\/[^/]+$/)) {
        permissionKey = 'purchase.orders'
      } else if (currentPath.match(/^\/purchase\/suppliers\/[^/]+$/)) {
        permissionKey = 'purchase.suppliers'
      }
    }

    // 권한 체크 - 권한 없으면 로그인 페이지로
    if (permissionKey && !hasMenuPermission(permissionKey)) {
      console.log(`[AppLayout] 권한 없음: ${permissionKey} → 로그인으로 리다이렉트`)
      navigate('/login', { replace: true })
    }
  }, [location.pathname, hasMenuPermission, navigate])

  // 메뉴 아이템을 권한에 따라 필터링
  const buildMenuItems = (): MenuProps['items'] => {
    // 설정 메뉴의 하위 항목
    const settingsChildren = [
      hasMenuPermission('settings.general') && {
        key: '/settings',
        icon: <SettingOutlined />,
        label: '일반 설정',
        onClick: () => navigate('/settings'),
      },
      hasMenuPermission('settings.approval') && {
        key: '/settings/approval',
        icon: <CheckCircleOutlined />,
        label: '사용자 승인',
        onClick: () => navigate('/settings/approval'),
      },
      hasMenuPermission('settings.users') && {
        key: '/settings/users',
        icon: <TeamOutlined />,
        label: '사용자 관리',
        onClick: () => navigate('/settings/users'),
      },
      hasMenuPermission('settings.permissions') && {
        key: '/settings/permissions',
        icon: <LockOutlined />,
        label: '권한 관리',
        onClick: () => navigate('/settings/permissions'),
      },
      // API 관리: 백엔드(apivault)와 같은 기준 — 슈퍼유저 또는 super_admin 역할만
      (user?.is_superuser || user?.profile_role === 'super_admin') && {
        key: '/settings/api-keys',
        icon: <ApiOutlined />,
        label: 'API 관리',
        onClick: () => navigate('/settings/api-keys'),
      },
      // 관리 전용 메뉴는 사이드바에 노출하지 않음 (다른 사람 노출 방지).
      // 진입은 /analyze URL → 게이트, 페이지 간 이동은 AnalyzeProtected wrapper 의 상단 토글 사용.
    ].filter(Boolean)

    // 주문 관리 하위 항목
    const ordersChildren = [
      hasMenuPermission('orders.dashboard') && {
        key: '/orders/dashboard',
        icon: <DashboardOutlined />,
        label: '대시보드',
        onClick: () => navigate('/orders/dashboard'),
      },
      hasMenuPermission('orders.list') && {
        key: '/orders',
        icon: <FileTextOutlined />,
        label: '주문서',
        onClick: () => navigate('/orders'),
      },
      hasMenuPermission('orders.pending') && {
        key: '/orders/pending',
        icon: <ClockCircleOutlined />,
        label: '배송대기',
        onClick: () => navigate('/orders/pending'),
      },
      hasMenuPermission('orders.verification') && {
        key: '/orders/verification',
        icon: <SafetyCertificateOutlined />,
        label: '통관부호검증',
        onClick: () => navigate('/orders/verification'),
      },
      hasMenuPermission('orders.inspected') && {
        key: '/orders/inspected',
        icon: <CheckCircleOutlined />,
        label: '검수완료',
        onClick: () => navigate('/orders/inspected'),
      },
      hasMenuPermission('orders.completed') && {
        key: '/orders/completed',
        icon: <CheckCircleOutlined />,
        label: '배송완료',
        onClick: () => navigate('/orders/completed'),
      },
      hasMenuPermission('orders.prepacked') && {
        key: '/orders/prepacked',
        icon: <GiftOutlined />,
        label: '선포장',
        onClick: () => navigate('/orders/prepacked'),
      },
      hasMenuPermission('orders.split_delivery') && {
        key: '/orders/split-delivery',
        icon: <SplitCellsOutlined />,
        label: '분할배송',
        onClick: () => navigate('/orders/split-delivery'),
      },
      hasMenuPermission('orders.3pl') && {
        key: '/orders/3pl',
        icon: <CloudServerOutlined />,
        label: '3PL',
        onClick: () => navigate('/orders/3pl'),
      },
      hasMenuPermission('orders.problem') && {
        key: '/orders/problem',
        icon: <ExclamationCircleOutlined />,
        label: '구매곤란',
        onClick: () => navigate('/orders/problem'),
      },
      hasMenuPermission('orders.unregistered') && {
        key: '/orders/unregistered',
        icon: <WarningOutlined />,
        label: '미등록',
        onClick: () => navigate('/orders/unregistered'),
      },
      hasMenuPermission('orders.hold') && {
        key: '/orders/hold',
        icon: <PauseCircleOutlined />,
        label: '보류',
        onClick: () => navigate('/orders/hold'),
      },
      hasMenuPermission('orders.cancelled') && {
        key: '/orders/cancelled',
        icon: <CloseCircleOutlined />,
        label: '취소',
        onClick: () => navigate('/orders/cancelled'),
      },
      hasMenuPermission('orders.trash') && {
        key: '/orders/trash',
        icon: <DeleteOutlined />,
        label: '휴지통',
        onClick: () => navigate('/orders/trash'),
      },
      hasMenuPermission('orders.inspection') && {
        key: '/orders/inspection',
        icon: <SearchOutlined />,
        label: '검수',
        onClick: () => navigate('/orders/inspection'),
      },
    ].filter(Boolean)

    // 발주 관리 하위 항목
    const purchaseChildren = [
      hasMenuPermission('purchase.dashboard') && {
        key: '/purchase/dashboard',
        icon: <DashboardOutlined />,
        label: '대시보드',
        onClick: () => navigate('/purchase/dashboard'),
      },
      hasMenuPermission('purchase.needed') && {
        key: '/purchase/needed',
        icon: <UnorderedListOutlined />,
        label: '발주 필요',
        onClick: () => navigate('/purchase/needed'),
      },
      hasMenuPermission('purchase.approval') && {
        key: '/purchase/approval',
        icon: <CheckCircleOutlined />,
        label: '발주 승인',
        onClick: () => navigate('/purchase/approval'),
      },
      hasMenuPermission('purchase.orders') && {
        key: '/purchase/orders',
        icon: <FileDoneOutlined />,
        label: '발주 목록',
        onClick: () => navigate('/purchase/orders'),
      },
      hasMenuPermission('purchase.incoming') && {
        key: '/purchase/incoming',
        icon: <ImportOutlined />,
        label: '입고 예정',
        onClick: () => navigate('/purchase/incoming'),
      },
      hasMenuPermission('purchase.internet') && {
        key: '/purchase/internet',
        icon: <ShoppingOutlined />,
        label: '인터넷사입 관리',
        onClick: () => navigate('/purchase/internet'),
      },
      hasMenuPermission('purchase.suppliers') && {
        key: '/purchase/suppliers',
        icon: <ShopOutlined />,
        label: '거래처 관리',
        onClick: () => navigate('/purchase/suppliers'),
      },
      hasMenuPermission('purchase.price_management') && {
        key: '/purchase/price-management',
        icon: <TagOutlined />,
        label: '가격 관리',
        onClick: () => navigate('/purchase/price-management'),
      },
    ].filter(Boolean)

    // 재고 관리 하위 항목
    const inventoryChildren = [
      hasMenuPermission('inventory.dashboard') && {
        key: '/inventory/dashboard',
        icon: <DashboardOutlined />,
        label: '대시보드',
        onClick: () => navigate('/inventory/dashboard'),
      },
      hasMenuPermission('inventory.products') && {
        key: '/inventory/products',
        icon: <AppstoreOutlined />,
        label: '제품 목록',
        onClick: () => navigate('/inventory/products'),
      },
      hasMenuPermission('inventory.products') && {
        key: '/inventory/stop-sale',
        icon: <StopOutlined />,
        label: '판매중지/재입고 알림',
        onClick: () => navigate('/inventory/stop-sale'),
      },
      hasMenuPermission('inventory.inbound') && {
        key: '/inventory/inbound',
        icon: <PlusCircleOutlined />,
        label: '입고',
        onClick: () => navigate('/inventory/inbound'),
      },
      hasMenuPermission('inventory.outbound') && {
        key: '/inventory/outbound',
        icon: <MinusCircleOutlined />,
        label: '출고',
        onClick: () => navigate('/inventory/outbound'),
      },
      hasMenuPermission('inventory.history') && {
        key: '/inventory/history',
        icon: <HistoryOutlined />,
        label: '재고 변동 이력',
        onClick: () => navigate('/inventory/history'),
      },
      hasMenuPermission('inventory.analysis') && {
        key: '/inventory/analysis',
        icon: <LineChartOutlined />,
        label: '재고 분석',
        onClick: () => navigate('/inventory/analysis'),
      },
      hasMenuPermission('inventory.past_quantity') && {
        key: '/inventory/past-quantity',
        icon: <CalendarOutlined />,
        label: '과거 수량 조회',
        onClick: () => navigate('/inventory/past-quantity'),
      },
      hasMenuPermission('inventory.adjustment') && {
        key: '/inventory/adjustment',
        icon: <SyncOutlined />,
        label: '재고 조정',
        onClick: () => navigate('/inventory/adjustment'),
      },
      hasMenuPermission('inventory.barcode_print') && {
        key: '/inventory/barcode-print',
        icon: <BarcodeOutlined />,
        label: '바코드 인쇄',
        onClick: () => navigate('/inventory/barcode-print'),
      },
      hasMenuPermission('inventory.barcode_mapping') && {
        key: '/inventory/barcode-mapping',
        icon: <BarcodeOutlined />,
        label: '바코드 매핑',
        onClick: () => navigate('/inventory/barcode-mapping'),
      },
      hasMenuPermission('inventory.data_management') && {
        key: '/inventory/data-management',
        icon: <DatabaseOutlined />,
        label: '데이터 관리',
        onClick: () => navigate('/inventory/data-management'),
      },
    ].filter(Boolean)

    // 전체 메뉴 구성
    const items: MenuProps['items'] = []

    // 홈
    if (hasMenuPermission('dashboard')) {
      items.push({
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: '홈',
        onClick: () => navigate('/dashboard'),
      })
    }

    // 마켓플레이스 메뉴 (super_admin only) - 사방넷 스타일
    // → 낱개로 흩뿌리지 않고 '주문서관리프로그램(예비)' 하나로 묶어 분석관리 아래 배치 (아래 참고)
    const marketplaceGroups: any[] = []
    if (user?.is_superuser) {
      const items = marketplaceGroups
      // 1. 쇼핑몰 관리
      items.push({
        key: 'marketplace-connect-group',
        icon: <ApiOutlined />,
        label: '쇼핑몰관리',
        children: [
          {
            key: '/marketplace/dashboard',
            icon: <DashboardOutlined />,
            label: '연동 현황',
            onClick: () => navigate('/marketplace/dashboard'),
          },
          {
            key: '/marketplace/accounts',
            icon: <LinkOutlined />,
            label: '계정 관리',
            onClick: () => navigate('/marketplace/accounts'),
          },
          {
            key: '/marketplace/extra-info',
            icon: <FileTextOutlined />,
            label: '부가정보 설정',
            onClick: () => navigate('/marketplace/extra-info'),
          },
        ],
      })

      // 2. 쇼핑몰 카테고리
      items.push({
        key: 'marketplace-category-group',
        icon: <FolderOutlined />,
        label: '쇼핑몰카테고리',
        children: [
          {
            key: '/marketplace/categories',
            icon: <FolderOutlined />,
            label: '카테고리 매핑',
            onClick: () => navigate('/marketplace/categories'),
          },
          {
            key: '/marketplace/attributes',
            icon: <TagOutlined />,
            label: '옵션/속성 관리',
            onClick: () => navigate('/marketplace/attributes'),
          },
        ],
      })

      // 3. 쇼핑몰 상품등록
      items.push({
        key: 'marketplace-register-group',
        icon: <PlusCircleOutlined />,
        label: '쇼핑몰상품등록',
        children: [
          {
            key: '/marketplace/register',
            icon: <PlusCircleOutlined />,
            label: '상품 등록',
            onClick: () => navigate('/marketplace/register'),
          },
          {
            key: '/marketplace/register/scheduled',
            icon: <CalendarOutlined />,
            label: '예약 등록 관리',
            onClick: () => navigate('/marketplace/register/scheduled'),
          },
          {
            key: '/marketplace/register/failed',
            icon: <CloseCircleOutlined />,
            label: '등록 실패',
            onClick: () => navigate('/marketplace/register/failed'),
          },
        ],
      })

      // 4. 쇼핑몰 상품관리
      items.push({
        key: 'marketplace-products-group',
        icon: <AppstoreOutlined />,
        label: '쇼핑몰상품관리',
        children: [
          {
            key: '/marketplace/products',
            icon: <AppstoreOutlined />,
            label: '상품 매핑',
            onClick: () => navigate('/marketplace/products'),
          },
          {
            key: '/marketplace/products/edit',
            icon: <EditOutlined />,
            label: '상품 수정',
            onClick: () => navigate('/marketplace/products/edit'),
          },
          {
            key: '/marketplace/products/soldout',
            icon: <ExclamationCircleOutlined />,
            label: '품절 관리',
            onClick: () => navigate('/marketplace/products/soldout'),
          },
          {
            key: '/marketplace/stock-sync',
            icon: <CloudSyncOutlined />,
            label: '재고 동기화',
            onClick: () => navigate('/marketplace/stock-sync'),
          },
        ],
      })

      // 5. 주문수집
      items.push({
        key: 'marketplace-orders-group',
        icon: <DownloadOutlined />,
        label: '주문수집',
        children: [
          {
            key: '/marketplace/orders',
            icon: <DownloadOutlined />,
            label: '주문서 수집(자동)',
            onClick: () => navigate('/marketplace/orders'),
          },
          {
            key: '/marketplace/orders/manual',
            icon: <EditOutlined />,
            label: '주문서 입력(수동)',
            onClick: () => navigate('/marketplace/orders/manual'),
          },
          {
            key: '/marketplace/orders/history',
            icon: <HistoryOutlined />,
            label: '수집 이력',
            onClick: () => navigate('/marketplace/orders/history'),
          },
          {
            key: '/marketplace/code-mapping',
            icon: <LinkOutlined />,
            label: '코드 매핑 관리',
            onClick: () => navigate('/marketplace/code-mapping'),
          },
        ],
      })

      // 6. 송장관리
      items.push({
        key: 'marketplace-tracking-group',
        icon: <UploadOutlined />,
        label: '송장관리',
        children: [
          {
            key: '/marketplace/tracking',
            icon: <UploadOutlined />,
            label: '송장 업로드',
            onClick: () => navigate('/marketplace/tracking'),
          },
          {
            key: '/marketplace/tracking/batch',
            icon: <CloudSyncOutlined />,
            label: '대량 송장 처리',
            onClick: () => navigate('/marketplace/tracking/batch'),
          },
          {
            key: '/marketplace/tracking/history',
            icon: <HistoryOutlined />,
            label: '송장 이력',
            onClick: () => navigate('/marketplace/tracking/history'),
          },
          {
            key: '/marketplace/tracking/failed',
            icon: <CloseCircleOutlined />,
            label: '전송 실패',
            onClick: () => navigate('/marketplace/tracking/failed'),
          },
        ],
      })

      // 6. 자동화/스케줄러
      items.push({
        key: 'marketplace-automation-group',
        icon: <ClockCircleOutlined />,
        label: '자동화/스케줄러',
        children: [
          {
            key: '/marketplace/scheduler',
            icon: <ClockCircleOutlined />,
            label: '스케줄러 설정',
            onClick: () => navigate('/marketplace/scheduler'),
          },
          {
            key: '/marketplace/scheduler/logs',
            icon: <FileTextOutlined />,
            label: '실행 이력',
            onClick: () => navigate('/marketplace/scheduler/logs'),
          },
        ],
      })
    }

    // 주문 관리
    if (hasMenuPermission('orders') && ordersChildren.length > 0) {
      items.push({
        key: 'orders-group',
        icon: <ShoppingCartOutlined />,
        label: '주문 관리',
        children: ordersChildren,
      })
    }

    // 발주 관리
    if (hasMenuPermission('purchase') && purchaseChildren.length > 0) {
      items.push({
        key: 'purchase-group',
        icon: <ShoppingOutlined />,
        label: '발주 관리',
        children: purchaseChildren,
      })
    }

    // 재고 관리
    if (hasMenuPermission('inventory') && inventoryChildren.length > 0) {
      items.push({
        key: 'inventory-group',
        icon: <ContainerOutlined />,
        label: '재고 관리',
        children: inventoryChildren,
      })
    }

    // 분석 관리
    items.push({
      key: 'analysis-group',
      icon: <BarChartOutlined />,
      label: '분석 관리',
      children: [
        {
          key: 'analysis-japan',
          label: '일본직구',
          children: [
            {
              key: 'analysis-japan-sales',
              icon: <LineChartOutlined />,
              label: '매출',
              children: [
                { key: '/analysis/japan/sales/summary',  label: '매출집계', onClick: () => navigate('/analysis/japan/sales/summary') },
                { key: '/analysis/japan/sales/analysis', label: '매출분석', onClick: () => navigate('/analysis/japan/sales/analysis') },
                { key: '/analysis/japan/sales/margin',   label: '마진계산', onClick: () => navigate('/analysis/japan/sales/margin') },
              ],
            },
            { key: '/analysis/japan/marketing', label: '마케팅', onClick: () => navigate('/analysis/japan/marketing') },
          ],
        },
        {
          key: 'analysis-metawell',
          label: '메타웰',
          children: [
            {
              key: 'analysis-metawell-sales',
              icon: <LineChartOutlined />,
              label: '매출',
              children: [
                { key: '/analysis/metawell/sales/summary',  label: '매출집계', onClick: () => navigate('/analysis/metawell/sales/summary') },
                { key: '/analysis/metawell/sales/analysis', label: '매출분석', onClick: () => navigate('/analysis/metawell/sales/analysis') },
                { key: '/analysis/metawell/sales/margin',   label: '마진계산', onClick: () => navigate('/analysis/metawell/sales/margin') },
              ],
            },
            {
              key: '/analysis/metawell/marketing',
              label: '마케팅',
              onClick: () => navigate('/analysis/metawell/marketing'),
            },
          ],
        },
        { key: '/analysis/settings', icon: <SettingOutlined />, label: '분석설정', onClick: () => navigate('/analysis/settings') },
      ],
    })

    // 주문서관리프로그램(예비) — marketplace 메뉴 전체를 하나로 묶어 분석관리 바로 아래에 배치 (기존 메뉴와 혼동 방지)
    if (user?.is_superuser && marketplaceGroups.length > 0) {
      items.push({
        key: 'oms-preview-group',
        icon: <AppstoreOutlined />,
        label: '주문서관리프로그램(예비)',
        children: marketplaceGroups,
      })
    }

    // 설정
    if (hasMenuPermission('settings') && settingsChildren.length > 0) {
      items.push({
        key: 'settings-group',
        icon: <SettingOutlined />,
        label: '설정',
        children: settingsChildren,
      })
    }

    return items
  }

  const menuItems = buildMenuItems()

  // 프로필 모달 상태
  const [profileModalOpen, setProfileModalOpen] = useState(false)

  // 사용자 드롭다운 메뉴
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '프로필',
      onClick: () => setProfileModalOpen(true),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '설정',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '로그아웃',
      danger: true,
      onClick: () => logout(),
    },
  ]

  // Breadcrumb 생성
  const getBreadcrumbItems = () => {
    const paths = location.pathname.split('/').filter(Boolean)
    const items: Array<{ title: string; onClick?: () => void }> = [
      {
        title: '홈',
        onClick: () => navigate('/dashboard'),
      },
    ]

    // /orders 경로일 때 "주문관리 / 주문서"로 표시
    if (paths.length === 1 && paths[0] === 'orders') {
      items.push({ title: '주문관리', onClick: () => navigate('/orders') })
      items.push({ title: '주문서' })
      return items
    }

    paths.forEach((path, index) => {
      // 숫자로만 이루어진 경로는 제품 상세 등의 ID이므로 "상세보기"로 표시
      if (/^\d+$/.test(path)) {
        items.push({
          title: '상세보기',
        })
        return
      }

      const url = `/${paths.slice(0, index + 1).join('/')}`
      const pathNameMap: Record<string, string> = {
        dashboard: '홈',
        orders: '주문관리',
        // 주문관리 하위 페이지
        pending: '배송대기',
        verification: '통관부호검증',
        inspected: '검수완료',
        completed: '배송완료',
        cancelled: '취소',
        prepacked: '선포장',
        'split-delivery': '분할배송',
        unregistered: '미등록',
        '3pl': '3PL',
        problem: '구매곤란',
        hold: '보류',
        trash: '휴지통',
        // 검수
        inspection: '검수',
        // 발주 관리
        purchase: '발주관리',
        needed: '발주필요',
        incoming: '입고예정',
        suppliers: '거래처',
        'purchase-orders': '발주서',
        approval: '발주승인',
        calendar: '발주달력',
        prices: '단가관리',
        'special-prices': '특별단가',
        'price-changes': '단가변동',
        // 재고 관리
        inventory: '재고관리',
        products: '제품목록',
        inbound: '입고',
        outbound: '출고',
        history: '히스토리',
        'past-quantity': '과거수량조회',
        adjustment: '재고조정',
        'barcode-print': '바코드인쇄',
        'barcode-mapping': '바코드매핑',
        'stock-adjustment-history': '재고변동이력',
        'data-management': '데이터관리',
        // 분석 관리
        analysis: '분석관리',
        japan: '일본직구',
        metawell: '메타웰',
        sales: '매출',
        summary: '매출집계',
        margin: '마진계산',
        // 설정
        settings: '설정',
        users: '사용자관리',
        // 쇼핑몰 연동
        marketplace: '쇼핑몰 연동',
        accounts: '계정 관리',
        tracking: '송장 업로드',
        'stock-sync': '재고 동기화',
      }

      const breadcrumbItem: { title: string; onClick?: () => void } = {
        title: pathNameMap[path] || path,
      }

      if (index !== paths.length - 1) {
        breadcrumbItem.onClick = () => navigate(url)
      }

      items.push(breadcrumbItem)
    })

    return items
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 8,
        },
      }}
    >
      <Layout style={{ minHeight: '100vh', minWidth: '1400px' }}>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          onBreakpoint={(broken) => {
            setCollapsed(broken)
          }}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 10,
          }}
        >
          <div
            style={{
              height: 32,
              margin: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: collapsed ? 16 : 20,
              fontWeight: 'bold',
            }}
          >
            {collapsed ? 'IC' : 'INCAPE'}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
          />
        </Sider>
        
        <Layout style={{
          marginLeft: collapsed ? 80 : 200,
          transition: 'margin 0.2s',
          minWidth: '1200px'
        }}>
          <Header
            style={{
              padding: '0 24px',
              background: isDarkMode ? '#141414' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: 0,
              zIndex: 9,
            }}
          >
            <Space>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  fontSize: '16px',
                  width: 64,
                  height: 64,
                }}
              />
            </Space>
            
            <Space size="middle">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => setSearchModalOpen(true)}
              >
                주문 검색
              </Button>

              <Switch
                checked={isDarkMode}
                onChange={setIsDarkMode}
                checkedChildren={<MoonOutlined />}
                unCheckedChildren={<SunOutlined />}
              />

              {/* 알림 아이콘 */}
              <Popover
                content={
                  <div style={{ width: 350, maxHeight: 400, overflow: 'auto' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                      borderBottom: '1px solid #f0f0f0',
                      paddingBottom: 8
                    }}>
                      <Text strong>알림</Text>
                      {unreadCount > 0 && (
                        <Button type="link" size="small" onClick={markAllAsRead}>
                          모두 읽음
                        </Button>
                      )}
                    </div>
                    {notificationLoading ? (
                      <div style={{ textAlign: 'center', padding: 20 }}>
                        <Spin size="small" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="알림이 없습니다"
                      />
                    ) : (
                      <List
                        dataSource={notifications}
                        renderItem={(item) => (
                          <List.Item
                            style={{
                              cursor: item.link_url ? 'pointer' : 'default',
                              background: item.is_read ? 'transparent' : (isDarkMode ? '#1a1a2e' : '#e6f7ff'),
                              padding: '8px 12px',
                              borderRadius: 4,
                              marginBottom: 4,
                            }}
                            onClick={() => handleNotificationClick(item)}
                          >
                            <List.Item.Meta
                              title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Text strong={!item.is_read} style={{ fontSize: 13 }}>
                                    {item.title}
                                  </Text>
                                  <Tag
                                    color={
                                      item.type === 'inbound' ? 'green' :
                                      item.type === 'low_stock' ? 'red' :
                                      item.type === 'po_approval' ? 'blue' : 'default'
                                    }
                                    style={{ fontSize: 10, marginLeft: 8 }}
                                  >
                                    {item.type_display}
                                  </Tag>
                                </div>
                              }
                              description={
                                <div>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {item.message}
                                  </Text>
                                  <br />
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {new Date(item.created_at).toLocaleString('ko-KR')}
                                  </Text>
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    )}
                  </div>
                }
                title={null}
                trigger="click"
                open={notificationPopoverOpen}
                onOpenChange={setNotificationPopoverOpen}
                placement="bottomRight"
              >
                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                  <Button
                    type="text"
                    icon={<BellOutlined style={{ fontSize: 18 }} />}
                    style={{ padding: '4px 8px' }}
                  />
                </Badge>
              </Popover>

              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar icon={<UserOutlined />} />
                  <span>{user?.last_name && user?.first_name ? `${user.last_name}${user.first_name}` : user?.username || 'User'}</span>
                </Space>
              </Dropdown>
            </Space>
          </Header>

          {/* 전체 탭 통합 검색 모달 */}
          <GlobalSearchModal
            open={searchModalOpen}
            onClose={() => setSearchModalOpen(false)}
          />
          
          <Content
            style={{
              margin: '24px',
              minHeight: 280,
              minWidth: '1150px',
            }}
          >
            <Breadcrumb
              items={getBreadcrumbItems()}
              style={{ marginBottom: 16 }}
            />
            
            <div
              style={{
                padding: 24,
                background: isDarkMode ? '#141414' : '#fff',
                borderRadius: 8,
                minHeight: '100%',
              }}
            >
              <Outlet />
            </div>
          </Content>
          
          <Footer style={{ textAlign: 'center' }}>
            INCAPE ©{new Date().getFullYear()} Created with React + Ant Design
          </Footer>
        </Layout>
      </Layout>

      {/* 프로필 모달 */}
      <Modal
        title="내 프로필"
        open={profileModalOpen}
        onCancel={() => setProfileModalOpen(false)}
        footer={null}
        width={500}
      >
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="사용자명">{user?.username}</Descriptions.Item>
          <Descriptions.Item label="이름">
            {user?.last_name && user?.first_name
              ? `${user.last_name}${user.first_name}`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="이메일">{user?.email || '-'}</Descriptions.Item>
          <Descriptions.Item label="권한">
            {user?.is_superuser ? (
              <Tag color="red">최고 관리자</Tag>
            ) : user?.is_staff ? (
              <Tag color="blue">관리자</Tag>
            ) : (
              <Tag>일반 사용자</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Modal>

      {/* 리소스 모니터 (superuser 전용) */}
      {user?.is_superuser && <ResourceMonitor />}
    </ConfigProvider>
  )
}